import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  Timestamp,
  writeBatch,
} from 'firebase/firestore';
import { firestore, isFirebaseEnabled } from './firebaseConfig';
import { InventoryItem } from '../redux/inventorySlice';
import { resolveDataScope, buildScopedPath } from './householdPaths';
import { isCookedLeftover } from '../utils/pantryLocation';

export interface SyncStatus {
  isSyncing: boolean;
  lastSyncTime: string | null;
  error: string | null;
}

/**
 * Firestore document interface
 */
interface FirestoreInventoryItem extends Omit<InventoryItem, 'dateAdded'> {
  dateAdded: string;
  updatedAt: Timestamp | string;
  userId: string;
}

/**
 * Get the inventory collection for the active scope (household when paired,
 * else the user's own).
 */
const getInventoryCollection = (
  userId: string,
  householdId?: string | null
) => {
  if (!firestore) {
    throw new Error('Firestore not initialized');
  }
  const scope = resolveDataScope(userId, householdId);
  if (!scope) {
    throw new Error('Cannot resolve data scope: missing userId and householdId');
  }
  const [root, id, ...rest] = buildScopedPath(scope, 'inventory');
  return collection(firestore, root, id, ...rest);
};

/**
 * Sync all inventory items to Firestore
 */
export const syncInventoryToCloud = async (
  userId: string,
  items: InventoryItem[],
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const batch = writeBatch(firestore);
  const inventoryRef = getInventoryCollection(userId, householdId);

  items.forEach((item) => {
    const docRef = doc(inventoryRef, item.id);
    // Exclude imageUri as it's not persisted (contains blob URLs)
    const { imageUri, ...itemWithoutImage } = item;
    const firestoreItem: FirestoreInventoryItem = {
      ...itemWithoutImage,
      userId,
      updatedAt: serverTimestamp() as Timestamp,
    };
    batch.set(docRef, firestoreItem, { merge: true });
  });

  await batch.commit();
  console.log(`✅ Synced ${items.length} items to cloud`);
};

/**
 * Fetch all inventory items from Firestore
 */
export const fetchInventoryFromCloud = async (
  userId: string,
  householdId?: string | null
): Promise<InventoryItem[]> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const inventoryRef = getInventoryCollection(userId, householdId);
  const snapshot = await getDocs(inventoryRef);

  const items: InventoryItem[] = snapshot.docs.map((doc) => {
    const data = doc.data() as FirestoreInventoryItem;
    // Remove Firestore-specific fields
    const { userId: _, updatedAt, ...item } = data;
    return item as InventoryItem;
  });

  console.log(`✅ Fetched ${items.length} items from cloud`);
  return items;
};

/**
 * Sync a single item to Firestore
 */
export const syncItemToCloud = async (
  userId: string,
  item: InventoryItem,
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const inventoryRef = getInventoryCollection(userId, householdId);
  const docRef = doc(inventoryRef, item.id);

  // Exclude imageUri as it's not persisted (contains blob URLs)
  const { imageUri, ...itemWithoutImage } = item;
  const firestoreItem: FirestoreInventoryItem = {
    ...itemWithoutImage,
    userId,
    updatedAt: serverTimestamp() as Timestamp,
  };

  await setDoc(docRef, firestoreItem, { merge: true });
  console.log(`✅ Synced item ${item.id} to cloud`);
};

/**
 * Delete an item from Firestore
 */
export const deleteItemFromCloud = async (
  userId: string,
  itemId: string,
  householdId?: string | null
): Promise<void> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const inventoryRef = getInventoryCollection(userId, householdId);
  const docRef = doc(inventoryRef, itemId);

  await deleteDoc(docRef);
  console.log(`✅ Deleted item ${itemId} from cloud`);
};

/**
 * Subscribe to real-time inventory updates
 */
export const subscribeToInventoryUpdates = (
  userId: string,
  callback: (items: InventoryItem[]) => void,
  householdId?: string | null
): (() => void) => {
  if (!isFirebaseEnabled() || !firestore) {
    console.warn('Firebase not enabled, subscription not available');
    return () => {};
  }

  const inventoryRef = getInventoryCollection(userId, householdId);

  return onSnapshot(
    inventoryRef,
    (snapshot) => {
      const items: InventoryItem[] = snapshot.docs.map((doc) => {
        const data = doc.data() as FirestoreInventoryItem;
        const { userId: _, updatedAt, ...item } = data;
        return item as InventoryItem;
      });

      console.log(`🔄 Real-time update: ${items.length} items`);
      callback(items);
    },
    (error) => {
      console.error('❌ Firestore subscription error:', error);
    }
  );
};

/**
 * Merge local and cloud inventory
 * Strategy: Last-write-wins based on lastModified timestamp
 * Falls back to dateAdded for backwards compatibility with items without lastModified
 */
export const mergeInventories = (
  localItems: InventoryItem[],
  cloudItems: InventoryItem[]
): InventoryItem[] => {
  const itemMap = new Map<string, InventoryItem>();

  // Add cloud items first
  cloudItems.forEach((item) => {
    itemMap.set(item.id, item);
  });

  // Merge with local items
  localItems.forEach((localItem) => {
    const cloudItem = itemMap.get(localItem.id);

    if (!cloudItem) {
      // Item only exists locally, add it
      itemMap.set(localItem.id, localItem);
    } else {
      // Item exists in both - use lastModified timestamp for conflict resolution
      // Fall back to dateAdded if lastModified doesn't exist (backwards compatibility)
      const localTimestamp = localItem.lastModified || localItem.dateAdded;
      const cloudTimestamp = cloudItem.lastModified || cloudItem.dateAdded;

      const localDate = new Date(localTimestamp).getTime();
      const cloudDate = new Date(cloudTimestamp).getTime();

      if (localDate > cloudDate) {
        // Local version is newer, use it
        itemMap.set(localItem.id, localItem);
        console.log(`  📝 Keeping local version of "${localItem.name}" (modified: ${localTimestamp})`);
      } else {
        // Cloud version is newer or equal, keep it
        console.log(`  ☁️ Keeping cloud version of "${cloudItem.name}" (modified: ${cloudTimestamp})`);
      }
    }
  });

  const mergedItems = Array.from(itemMap.values());
  console.log(`✅ Merged inventory: ${mergedItems.length} total items`);
  return mergedItems;
};

/**
 * Subscribe to real-time inventory changes from Firestore
 * Returns an unsubscribe function to clean up the listener
 */
export const subscribeToInventoryChanges = (
  userId: string,
  onUpdate: (items: InventoryItem[]) => void,
  onError?: (error: Error) => void,
  householdId?: string | null
): (() => void) => {
  if (!isFirebaseEnabled() || !firestore) {
    console.warn('Firebase not enabled - real-time sync unavailable');
    return () => {}; // Return no-op unsubscribe
  }

  const inventoryRef = getInventoryCollection(userId, householdId);

  console.log(`🔄 Subscribing to real-time inventory updates for user ${userId}`);

  const unsubscribe = onSnapshot(
    inventoryRef,
    (snapshot) => {
      const items: InventoryItem[] = snapshot.docs.map((doc) => {
        const data = doc.data() as FirestoreInventoryItem;
        // Remove Firestore-specific fields
        const { userId: _, updatedAt, ...item } = data;
        return item as InventoryItem;
      });

      console.log(`✅ Real-time update received: ${items.length} items`);
      onUpdate(items);
    },
    (error) => {
      console.error('❌ Real-time sync error:', error);
      if (onError) {
        onError(error);
      }
    }
  );

  return unsubscribe;
};

/**
 * Clear user's cloud inventory (useful for testing or account deletion)
 */
export const clearCloudInventory = async (
  userId: string,
  householdId?: string | null
): Promise<number> => {
  if (!isFirebaseEnabled() || !firestore) {
    throw new Error('Firebase not enabled');
  }

  const inventoryRef = getInventoryCollection(userId, householdId);
  const snapshot = await getDocs(inventoryRef);

  // Only what is on the shelves. Eaten and discarded rows are the consumption
  // history that waste metrics read from — clearing a cluttered pantry should
  // not erase months of it.
  //
  // Cooked leftovers are spared too (#663). "Vider le garde-manger" means the
  // ingredients have got out of hand, not "throw out the meals I cooked and
  // froze". This must stay in step with `clearInventory` in inventorySlice: if
  // the cloud deleted them while local kept them, the realtime listener would
  // delete them locally moments later and the exclusion would look broken.
  const targets = snapshot.docs.filter((doc) => {
    const data = doc.data() as { status?: string; source?: string };
    if (isCookedLeftover(data)) return false;
    return data.status === undefined || data.status === 'active';
  });

  // Firestore caps a WriteBatch at 500 operations and rejects the batch whole,
  // so a single batch fails on exactly the large pantries worth clearing.
  const BATCH_LIMIT = 450;
  const commits: Array<Promise<void>> = [];
  for (let i = 0; i < targets.length; i += BATCH_LIMIT) {
    const batch = writeBatch(firestore);
    targets.slice(i, i + BATCH_LIMIT).forEach((doc) => batch.delete(doc.ref));
    commits.push(batch.commit());
  }
  await Promise.all(commits);

  console.log(`✅ Cleared ${targets.length} pantry items for user ${userId}`);

  // Returned so the caller can tell "nothing to clear" from "cleared nothing
  // because we read the wrong scope" — see clearInventoryAndSync.
  return targets.length;
};
